import React from 'react';
import { useAdminFetch } from '@/lib/adminApi';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import LoginLineChart from '@/components/admin/LoginLineChart';

interface UserStats {
  mostActiveUsers: Array<{ userId: string; userName: string; activityCount: number }>;
  topUploaders: Array<{ userId: string; userName: string; uploadCount: number }>;
  topSharers: Array<{ userId: string; userName: string; shareCount: number }>;
  loginStats: Array<{ date: string; count: number }>;
}

export function UsersPage() {
  const [stats, setStats] = React.useState<UserStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [timeRange, setTimeRange] = React.useState(30);
  const adminGet = useAdminFetch();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('tr') ? tr : enUS;

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await adminGet<UserStats>(`/admin/user-stats?days=${timeRange}`);
        setStats(data);
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [timeRange, adminGet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-muted-foreground">{t('admin.loadFailed')}</div>
    );
  }

  const loginChartData = {
    labels: stats.loginStats.map(stat =>
      format(new Date(stat.date), 'dd MMM', { locale: dateLocale })
    ),
    datasets: [
      {
        label: t('admin.users.chartLabel'),
        data: stats.loginStats.map(stat => stat.count),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: t('admin.users.chartTitle'),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div className="space-y-6">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('admin.users.title')}</h1>
            <p className="mt-2 text-sm text-foreground">
              {t('admin.users.subtitle')}
            </p>
          </div>
          <div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="mt-1 block w-full rounded-md border-border-color shadow-sm focus:border-accent focus:ring-accent sm:text-sm"
            >
              <option value={7}>{t('admin.users.range7')}</option>
              <option value={30}>{t('admin.users.range30')}</option>
              <option value={90}>{t('admin.users.range90')}</option>
            </select>
          </div>
        </div>

        <div className="bg-card p-6 rounded-lg shadow">
          <LoginLineChart data={loginChartData} options={chartOptions} />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="bg-card shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium text-foreground">{t('admin.users.mostActive')}</h3>
            </div>
            <div className="border-t border-border-color">
              <ul role="list" className="divide-y divide-border-color">
                {stats.mostActiveUsers.map((user) => (
                  <li key={user.userId} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-foreground">{user.userName}</div>
                      <div className="text-sm text-muted-foreground">{t('admin.users.activityCount', { count: user.activityCount })}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-card shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium text-foreground">{t('admin.users.topUploaders')}</h3>
            </div>
            <div className="border-t border-border-color">
              <ul role="list" className="divide-y divide-border-color">
                {stats.topUploaders.map((user) => (
                  <li key={user.userId} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-foreground">{user.userName}</div>
                      <div className="text-sm text-muted-foreground">{t('admin.users.uploadCount', { count: user.uploadCount })}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-card shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium text-foreground">{t('admin.users.topSharers')}</h3>
            </div>
            <div className="border-t border-border-color">
              <ul role="list" className="divide-y divide-border-color">
                {stats.topSharers.map((user) => (
                  <li key={user.userId} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-foreground">{user.userName}</div>
                      <div className="text-sm text-muted-foreground">{t('admin.users.shareCount', { count: user.shareCount })}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
    </div>
  );
}
